<template>
  <!-- 归属公司树选：下拉面板展示组织树，仅公司节点可选（部门灰显不可点）。 -->
  <div class="ctp" v-clickoutside="close">
    <button
      ref="trigger"
      type="button"
      class="ctp-trigger"
      :class="{ 'is-open': open, 'is-placeholder': !selectedLabel }"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="ctp-trigger__text">{{ selectedLabel || placeholder }}</span>
      <i class="fa ctp-trigger__caret" :class="open ? 'fa-angle-up' : 'fa-angle-down'"></i>
    </button>

    <!-- fixed 挂到视口，避免弹窗 overflow 裁剪（对齐 OrgList ⋯ 菜单） -->
    <div
      v-show="open"
      class="ctp-dropdown ctp-dropdown--fixed"
      :style="dropdownStyle"
      @click.stop
    >
      <div class="ctp-search">
        <input
          ref="kw"
          class="ctp-search__input"
          v-model.trim="keyword"
          type="text"
          placeholder="筛选公司名称"
          @keydown.esc.prevent="close"
        />
      </div>
      <div class="ctp-body" v-if="visibleRoots.length">
        <company-tree-node
          v-for="n in visibleRoots"
          :key="n.id"
          :node="n"
          :depth="0"
          :selected-id="value"
          :expanded="expanded"
          :match-ids="matchIds"
          @toggle="toggleExpand"
          @pick="pick"
        />
      </div>
      <div v-else class="ctp-empty">{{ keyword ? '无匹配公司' : '暂无公司' }}</div>
    </div>
  </div>
</template>

<script>
function isCompany(n) {
	return String((n && (n.nodeType || n.node_type)) || 'COMPANY').toUpperCase() === 'COMPANY';
}

/** 保留含匹配公司的子树；部门仅作结构展示，匹配只看公司名。 */
function filterCompanyTree(nodes, kw) {
	const q = (kw || '').trim().toLowerCase();
	if (!q) return nodes || [];
	const walk = (list) => {
		const out = [];
		(list || []).forEach((n) => {
			const kids = walk(n.children || []);
			const selfHit = isCompany(n) && String(n.name || '').toLowerCase().indexOf(q) >= 0;
			if (selfHit || kids.length) {
				out.push(Object.assign({}, n, { children: kids }));
			}
		});
		return out;
	};
	return walk(nodes);
}

function collectMatchCompanyIds(nodes, kw, set = new Set()) {
	const q = (kw || '').trim().toLowerCase();
	if (!q) return set;
	(nodes || []).forEach((n) => {
		if (isCompany(n) && String(n.name || '').toLowerCase().indexOf(q) >= 0) set.add(n.id);
		if (n.children && n.children.length) collectMatchCompanyIds(n.children, kw, set);
	});
	return set;
}

function findCompanyPath(nodes, id, trail = []) {
	for (let i = 0; i < (nodes || []).length; i++) {
		const n = nodes[i];
		const next = trail.concat([n.name]);
		if (String(n.id) === String(id) && isCompany(n)) return next;
		const hit = findCompanyPath(n.children || [], id, isCompany(n) ? next : trail);
		if (hit) return hit;
	}
	return null;
}

function collectExpandableIds(nodes, out = new Set()) {
	(nodes || []).forEach((n) => {
		if (n.children && n.children.length) {
			out.add(n.id);
			collectExpandableIds(n.children, out);
		}
	});
	return out;
}

const CompanyTreeNode = {
	name: 'CompanyTreeNode',
	props: {
		node: { type: Object, required: true },
		depth: { type: Number, default: 0 },
		selectedId: { default: null },
		expanded: { type: Object, default: () => ({}) },
		matchIds: { type: [Object, Set], default: () => new Set() }
	},
	computed: {
		company() { return isCompany(this.node); },
		hasChildren() { return !!(this.node.children && this.node.children.length); },
		opened() { return !!this.expanded[this.node.id]; },
		selected() { return this.company && String(this.selectedId) === String(this.node.id); },
		matched() { return this.matchIds && this.matchIds.has && this.matchIds.has(this.node.id); }
	},
	methods: {
		onToggle(e) {
			e.stopPropagation();
			if (this.hasChildren) this.$emit('toggle', this.node.id);
		},
		onPick() {
			if (!this.company) return;
			this.$emit('pick', this.node);
		}
	},
	render(h) {
		const pad = 10 + this.depth * 14;
		const row = h('div', {
			class: {
				'ctp-node': true,
				'is-company': this.company,
				'is-dept': !this.company,
				'is-selected': this.selected,
				'is-matched': this.matched,
				'is-disabled': !this.company
			},
			style: { paddingLeft: pad + 'px' },
			on: { click: this.onPick }
		}, [
			h('button', {
				attrs: { type: 'button', tabindex: '-1', title: this.hasChildren ? (this.opened ? '收起' : '展开') : '' },
				class: { 'ctp-expand': true, 'is-leaf': !this.hasChildren, 'is-open': this.opened },
				on: { click: this.onToggle }
			}, [this.hasChildren ? h('i', { class: 'fa fa-caret-right' }) : null]),
			h('span', {
				class: ['ctp-ico', this.company ? 'ctp-ico--company' : 'ctp-ico--dept'],
				attrs: { 'aria-hidden': 'true' }
			}, [
				// 与 OrgList 一致：公司 fa-building / 部门 fa-folder
				h('i', { class: ['fa', this.company ? 'fa-building' : 'fa-folder'] })
			]),
			h('span', { class: 'ctp-name' }, this.node.name),
			h('span', {
				class: ['ctp-badge', this.company ? 'ctp-badge--company' : 'ctp-badge--dept']
			}, this.company ? '公司' : '部门')
		]);

		const kids = (this.opened && this.hasChildren)
			? this.node.children.map((c) => h('company-tree-node', {
				key: c.id,
				props: {
					node: c,
					depth: this.depth + 1,
					selectedId: this.selectedId,
					expanded: this.expanded,
					matchIds: this.matchIds
				},
				on: {
					toggle: (id) => this.$emit('toggle', id),
					pick: (n) => this.$emit('pick', n)
				}
			}))
			: [];

		return h('div', { class: 'ctp-branch' }, [row].concat(kids));
	}
};

export default {
	name: 'CompanyTreePicker',
	components: { CompanyTreeNode },
	props: {
		value: { type: [Number, String], default: null },
		/** 嵌套组织树（含 COMPANY/DEPT） */
		tree: { type: Array, default: () => [] },
		placeholder: { type: String, default: '请选择公司' },
		disabled: { type: Boolean, default: false }
	},
	data() {
		return {
			open: false,
			keyword: '',
			expanded: {},
			dropdownStyle: {}
		};
	},
	computed: {
		selectedLabel() {
			if (this.value == null || this.value === '') return '';
			const path = findCompanyPath(this.tree, this.value);
			return path ? path.join(' / ') : '';
		},
		visibleRoots() {
			return filterCompanyTree(this.tree, this.keyword);
		},
		matchIds() {
			return collectMatchCompanyIds(this.tree, this.keyword);
		}
	},
	watch: {
		tree: {
			immediate: true,
			handler(v) {
				// 默认展开第一层有子节点的公司，便于点选
				const next = {};
				(v || []).forEach((n) => {
					if (n.children && n.children.length) next[n.id] = true;
				});
				this.expanded = next;
			}
		},
		keyword(v) {
			if (!(v || '').trim()) return;
			// 筛选时自动展开可见分支
			const ids = collectExpandableIds(this.visibleRoots);
			const next = Object.assign({}, this.expanded);
			ids.forEach((id) => { next[id] = true; });
			this.expanded = next;
		},
		value(v) {
			if (v == null || v === '') return;
			this.ensurePathExpanded(v);
		}
	},
	directives: {
		clickoutside: {
			bind(el, binding) {
				el._ctpClick = (e) => { if (!el.contains(e.target)) binding.value(); };
				document.addEventListener('click', el._ctpClick);
			},
			unbind(el) { document.removeEventListener('click', el._ctpClick); }
		}
	},
	mounted() {
		this._onWin = () => { if (this.open) this.updateDropdownPos(); };
		window.addEventListener('resize', this._onWin);
		window.addEventListener('scroll', this._onWin, true);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this._onWin);
		window.removeEventListener('scroll', this._onWin, true);
	},
	methods: {
		toggle() {
			if (this.disabled) return;
			this.open = !this.open;
			if (this.open) {
				this.updateDropdownPos();
				this.$nextTick(() => {
					this.updateDropdownPos();
					if (this.$refs.kw) this.$refs.kw.focus();
				});
			}
		},
		close() { this.open = false; },
		updateDropdownPos() {
			const el = this.$refs.trigger;
			if (!el) return;
			const r = el.getBoundingClientRect();
			const width = Math.max(r.width, 280);
			const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
			const spaceBelow = window.innerHeight - r.bottom - 8;
			const preferBelow = spaceBelow >= 220 || spaceBelow >= r.top;
			const top = preferBelow ? r.bottom + 4 : Math.max(8, r.top - 4);
			this.dropdownStyle = {
				position: 'fixed',
				top: preferBelow ? `${top}px` : 'auto',
				bottom: preferBelow ? 'auto' : `${window.innerHeight - r.top + 4}px`,
				left: `${left}px`,
				width: `${width}px`,
				zIndex: 1300
			};
		},
		toggleExpand(id) {
			this.$set(this.expanded, id, !this.expanded[id]);
		},
		ensurePathExpanded(id) {
			const expandAncestors = (nodes, trail = []) => {
				for (let i = 0; i < (nodes || []).length; i++) {
					const n = nodes[i];
					if (String(n.id) === String(id)) {
						trail.forEach((aid) => this.$set(this.expanded, aid, true));
						return true;
					}
					if (n.children && n.children.length) {
						if (expandAncestors(n.children, trail.concat([n.id]))) return true;
					}
				}
				return false;
			};
			expandAncestors(this.tree);
		},
		pick(node) {
			if (!isCompany(node)) return;
			this.$emit('input', node.id);
			this.$emit('change', node);
			this.open = false;
			this.keyword = '';
		}
	}
};
</script>

<style lang="less" scoped>
.ctp { position: relative; display: block; width: 100%; }
.ctp-trigger {
	width: 100%;
	height: 32px;
	padding: 0 28px 0 10px;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	background: #fff;
	text-align: left;
	cursor: pointer;
	outline: none;
	position: relative;
	&:hover { border-color: #c0c4cc; }
	&:focus, &.is-open { border-color: #3788ee; }
	&:disabled { background: #f5f7fa; color: #c0c4cc; cursor: not-allowed; }
}
.ctp-trigger__text {
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 13px;
	color: #1f2937;
	line-height: 30px;
}
.ctp-trigger.is-placeholder .ctp-trigger__text { color: #c0c4cc; }
.ctp-trigger__caret {
	position: absolute;
	right: 10px;
	top: 50%;
	transform: translateY(-50%);
	color: #9aa4b2;
	font-size: 12px;
}
.ctp-dropdown {
	background: #fff;
	border: 1px solid #e4e7ed;
	border-radius: 6px;
	box-shadow: 0 8px 24px rgba(15, 23, 32, 0.12);
	overflow: hidden;
}
.ctp-dropdown--fixed {
	/* top/left/width 由 JS 写入，避免弹窗 overflow 裁剪 */
}
.ctp-search {
	padding: 8px;
	border-bottom: 1px solid #eef0f3;
	background: #fafbfc;
}
.ctp-search__input {
	width: 100%;
	height: 30px;
	padding: 0 10px;
	border: 1px solid #dcdfe6;
	border-radius: 4px;
	outline: none;
	font-size: 13px;
	&:focus { border-color: #3788ee; }
}
.ctp-body {
	max-height: 280px;
	overflow: auto;
	padding: 6px 0;
}
.ctp-empty {
	padding: 16px 10px;
	text-align: center;
	color: #9aa4b2;
	font-size: 13px;
}

/* 节点行：子组件 render 产出，需 deep */
.ctp /deep/ .ctp-node {
	display: flex;
	align-items: center;
	gap: 6px;
	min-height: 32px;
	padding-right: 10px;
	font-size: 13px;
	color: #374151;
	cursor: default;
	user-select: none;
}
.ctp /deep/ .ctp-node.is-company { cursor: pointer; }
.ctp /deep/ .ctp-node.is-company:hover { background: #f5f9ff; }
.ctp /deep/ .ctp-node.is-selected {
	background: #eff6ff;
	color: #1d4ed8;
}
.ctp /deep/ .ctp-node.is-matched .ctp-name { font-weight: 600; }
.ctp /deep/ .ctp-node.is-dept { color: #9aa4b2; }
.ctp /deep/ .ctp-expand {
	width: 16px;
	height: 16px;
	border: 0;
	padding: 0;
	background: transparent;
	color: #9aa4b2;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	flex: none;
	&.is-leaf { visibility: hidden; cursor: default; }
	&.is-open i { transform: rotate(90deg); }
	i { transition: transform .12s ease; font-size: 12px; }
}
/* 与 OrgList.org-icon 同色：公司淡蓝 / 部门浅琥珀黄 */
.ctp /deep/ .ctp-ico {
	width: 22px;
	height: 22px;
	border-radius: 6px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	line-height: 1;
	flex: none;
}
.ctp /deep/ .ctp-ico--company {
	background: #e8f0ff;
	color: #7aa6ff;
}
.ctp /deep/ .ctp-ico--dept {
	background: #eef6f0;
	color: #fee7b8;
}
.ctp /deep/ .ctp-ico .fa { color: inherit; }
.ctp /deep/ .ctp-name {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.ctp /deep/ .ctp-badge {
	flex: none;
	height: 18px;
	padding: 0 6px;
	border-radius: 999px;
	font-size: 11px;
	line-height: 18px;
}
.ctp /deep/ .ctp-badge--company {
	color: #1d4ed8;
	background: #eff6ff;
}
.ctp /deep/ .ctp-badge--dept {
	color: #6b7280;
	background: #f3f4f6;
}
</style>
